import { UntypedFormGroup } from '@angular/forms';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FileUpload } from 'primeng/fileupload';
import { MessageUtil } from 'src/app/utils/message.util';
import { SailorService } from 'src/app/services/sailor.service';
import { LocationService } from 'src/app/services/location.service';
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-sailor-license',
  templateUrl: './sailor-license.component.html',
  styleUrls: ['./sailor-license.component.scss'],
})
export class SailorLicenseComponent implements OnInit {
  @ViewChild('upload', { static: false }) upload: FileUpload;

  @Input()
  licenseForm = new UntypedFormGroup({});

  @Output()
  finishEvent: EventEmitter<any> = new EventEmitter();

  file: File;
  imgPreview: any;

  typesLicense = [
    { label: 'Selecione...' },
    { label: 'Veleiro', value: 'SAILBOAT' },
    { label: 'Motonauta', value: 'MOTONAUTA' },
    { label: 'Arrais-Amador', value: 'ARRAIS_AMATEUR' },
    { label: 'Mestre-Amador', value: 'MASTER_AMATEUR' },
    { label: 'Capitão-Amador', value: 'AMATEUR_CAPTAIN' },
    { label: 'Arrais-Amador e Motonauta', value: 'ARRAIS_AMATEUR_MOTONAUTA' },
    { label: 'Mestre-Amador e Motonauta', value: 'MASTER_AMATEUR_MOTONAUTA' },
    { label: 'Capitão-Amador e Motonauta', value: 'AMATEUR_CAPTAIN_MOTONAUTA' },
    { label: 'Carta de Marinheiro', value: 'MARINER_CARD' },
    { label: 'Carta de Patrão Local', value: 'LOCAL_PATROL_CARD' },
    { label: 'Carta de Patrão de Costa', value: 'COAST_PATROL_CARD' },
    { label: 'Carta de Patrão de Alto Mar', value: 'HIGH_SEA_PATROL_CARD' }
  ];

  errorMessage: string;
  countries: any[];
  states: any[];
  cities: any[];
  private isLoadingInitialData = false;
  private isLoadingStates = false;
  private isLoadingCities = false;

  constructor(
    private locationService: LocationService,
    private messageUtil: MessageUtil,
    private sailorService: SailorService
  ) {}

  ngOnInit(): void {
    if (this.licenseForm.get('id').value == null) {
      this.licenseForm.get('country').setValue('BR');
    }

    this.loadCountries();
    this.setupFormSubscriptions();
    this.imgPreview = this.licenseForm.get("file").value;
    this.enviar();
  }

  private setupFormSubscriptions(): void {

    this.licenseForm.get('country').valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(100)
    ).subscribe(countryCode => {
      if (countryCode && !this.isLoadingInitialData && !this.isLoadingStates) {

        this.licenseForm.patchValue({
          state: null,
          city: null
        }, { emitEvent: false });
        this.states = [];
        this.cities = [];

        const countryName = this.getCountryNameByCountryIso2(countryCode);
        if (countryName) {
          this.loadStates(countryName, false);
        }
      }
    });

    this.licenseForm.get('state').valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(100)
    ).subscribe(stateCode => {
      if (stateCode && !this.isLoadingInitialData && !this.isLoadingCities) {

        this.licenseForm.patchValue({
          city: null
        }, { emitEvent: false });
        this.cities = [];

        const countryCode = this.licenseForm.get('country').value;
        const countryName = this.getCountryNameByCountryIso2(countryCode);
        const stateName = this.getStateNameByCode(stateCode);
        if (countryName && stateName) {
          this.loadCities(countryName, stateName, false);
        }
      }
    });

    this.licenseForm.valueChanges.subscribe(change => this.enviar());
  }

  enviar() {
    this.finishEvent.emit(this.licenseForm);
  }

  loadCountries(): void {
    try {
      this.isLoadingInitialData = true;
      this.countries = [{ label: 'Selecione...', value: null }].concat(
        this.locationService.getCountries().map((c) => ({ label: c.name, value: c.code }))
      );

      if (this.licenseForm.get('country').value) {
        this.loadStates(this.licenseForm.get('country').value, true); 
      } else {
        this.isLoadingInitialData = false;
      }
    } catch (error) {
      console.error('Erro buscando países:', error);
      this.resetLocationData();
      this.isLoadingInitialData = false;
    }
  }

  getCountryNameByCountryIso2(iso2: string): string {
    const country = this.countries?.find(c => c.value === iso2);
    if (!country) return '';

    this.licenseForm.get('country').setValue(country.value);
    return country.label;
  }

  getStateNameByCode(stateCode: string): string {
    const state = this.states?.find(s => s.value === stateCode);
    return state ? state.label : '';
  }

  loadStates(countryId: string, preserveExistingData: boolean = false): void {
    if (!countryId) return;

    if (this.isLoadingStates) return; 
    this.isLoadingStates = true;

    try {
      // Usando LocationService para obter estados
      this.states = [{ label: 'Selecione...', value: null }].concat(
        this.locationService.getStates().map((s) => ({ label: s.name, value: s.id }))
      );

      const currentState = this.licenseForm.get('state').value;
      if (currentState && preserveExistingData) {
        const stateExists = this.states.some(state => 
          state.value === currentState
        );

        // Se o estado não existe na lista (ex: estado estrangeiro não listado em BrStates), 
        // mantemos ele se já estiver salvo, mas a lista de opções será apenas BR por enquanto
        // devido à limitação do LocationService.getStates()
        if (!stateExists) {
          this.states.push({
            label: currentState,
            value: currentState
          });
        }
        
        // Carrega cidades usando IDs
        this.loadCities(countryId, currentState, true);
      } else if (preserveExistingData) {
        this.isLoadingInitialData = false;
      }
      this.isLoadingStates = false;

    } catch (error) {
      console.error('Erro buscando estados:', error);
      this.resetLocationData();
      this.isLoadingStates = false;
    }
  }

  loadCities(countryId: string, stateId: string, preserveExistingData: boolean = false): void {
    if (!countryId || !stateId) return;

    if (this.isLoadingCities) return;
    this.isLoadingCities = true;

    const currentCity = preserveExistingData ? this.licenseForm.get('city').value : null;
    const currentStateCode = this.licenseForm.get('state').value;
    const actualStateName = this.getStateNameByCode(currentStateCode) || stateId;

    // Usando LocationService para obter cidades
    this.locationService.getCities(countryId, stateId).subscribe({
      next: (data) => {
        this.cities = data.map((c) => ({ label: c.name, value: c.name }));

        if (currentCity) {
          const cityExists = this.cities.some(city =>
            city.value.toLowerCase() === currentCity.toLowerCase()
          );

          if (!cityExists) {
            this.cities.push({
              label: currentCity,
              value: currentCity
            });
          }

          setTimeout(() => {
            this.licenseForm.patchValue({
              city: currentCity
            });
            if (preserveExistingData) {
              this.isLoadingInitialData = false;
            }
            this.isLoadingCities = false;
          }, 100);
        } else {
          this.isLoadingCities = false;
        }
      },
      error: (error) => {
        console.error('Erro buscando cidades:', error);

        this.cities = [{
          label: actualStateName,
          value: actualStateName
        }];

        const cityToSet = currentCity || actualStateName;

        if (currentCity && currentCity !== actualStateName) {
          this.cities.push({
            label: currentCity,
            value: currentCity
          });
        }

        setTimeout(() => {
          this.licenseForm.patchValue({
            city: cityToSet
          });
          if (preserveExistingData) {
            this.isLoadingInitialData = false;
          }
          this.isLoadingCities = false;
        }, 100);
      }
    });
  }

  private resetLocationData(): void {
    this.states = [];
    this.cities = [];
  }

  receiveUploadUrl($event) {
    this.licenseForm.get("file").setValue($event);
    this.enviar();
  }
}
